import React, {
  useState,
  useMemo,
} from 'react';
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import useUserInfo from 'pages/Dashboard/hooks/useUserInfo';
import {
  PatientForm,
} from 'pages/Dashboard/types/patient';
import ToolTip from 'core/components/Tooltip';
import Dialog from '@mui/material/Dialog';
import Button from 'pages/Dashboard/components/Button';
import MedicationsIcon from 'core/icons/medications';
import WebPage from 'pages/Dashboard/pages/Charts/pages/Details/components/ERx/WebPage';
import Badge from 'core/components/Badge';

type Props = {
  className?: string;
  text?: string;
  patient?: PatientForm;
  onClick?: Function;
};

export default function ERxButton({
  className,
  text = 'Prescriptions',
  patient,
  onClick,
}: Props) {
  const [open, setOpen] = useState<boolean>(false);
  const userInfo = useUserInfo();
  const eRxEnabled = userInfo?.flags?.haseRxCredentials ?? false;

  const openDosespotDialog = () => setOpen(true);

  const hasRequiredInfo = useMemo(() => (
    isNil(patient)
      ? true
      : isNil(patient.dosespotError)
  ), [patient]);

  const tooltipText = useMemo(
    () => {
      if (!eRxEnabled) {
        return 'eRx feature is not enabled for this user account.';
      }

      if (!hasRequiredInfo) {
        return patient?.dosespotError?.title ?? '';
      }

      return null;
    },
    [eRxEnabled, hasRequiredInfo],
  );

  return (
    <>
      <ToolTip
        title={tooltipText}
        hide={hasRequiredInfo && eRxEnabled}
        placement="top"
      >
        <span className={clsx('cursor-pointer inline-block !w-min', className)}>
          <Badge
            invisible={hasRequiredInfo && eRxEnabled}
            badgeContent="!"
            color="error"
            classes={{
              badge: 'cursor-pointer',
            }}
          >
            <Button
              variant="outlined"
              type="button"
              color="secondary"
              className={clsx('!h-8', {
                '!border-red-600 !text-red-600': !hasRequiredInfo,
              })}
              onClick={() => (!isNil(onClick) ? onClick : openDosespotDialog)()}
              startIcon={<MedicationsIcon className="!w-4 !h-4 !text-inherit" />}
            >
              {text}
            </Button>
          </Badge>
        </span>
      </ToolTip>
      <Dialog
        className="populate-iframe-modal !h-full"
        hideBackdrop
        maxWidth={false}
        classes={{ paper: '!m-0 !w-full !h-full !max-h-[initial] !max-w-[initial] flex justify-end flex-col' }}
        open={open}
        onClose={(event, reason) => {
          if (reason !== 'backdropClick') {
            setOpen(false);
          }
        }}
      >
        {open && <WebPage patient={patient} />}
        <div className="flex flex-row items-center w-full h-12 p-2 justify-end bg-gray-100 border-t border-gray-300">
          <Button
            variant="outlined"
            onClick={() => { setOpen(false); }}
          >
            Close
          </Button>
        </div>
      </Dialog>
    </>
  );
}
